import React from 'react';
import { ArrowLeft, Brain } from 'lucide-react';

const PrivacyPolicy = ({ onBack }) => {
  return (
    <div className="min-h-screen" style={{ background: '#0D1117' }}>
      {/* Header */}
      <div className="py-8" style={{ background: '#111827', borderBottom: '1px solid #1F2937' }}>
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft size={18} />
            <span>Back to Dashboard</span>
          </button>
          <div className="flex items-center space-x-3">
            <Brain size={28} className="text-blue-400" />
            <div>
              <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
              <p className="text-gray-400 text-sm mt-1">Last Updated: February 25, 2026</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-10 space-y-8 text-gray-700 leading-relaxed">

        {/* Intro */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            1. Introduction
          </h2>
          <p>
            Krish Automation Services ("we", "our", or "us") operates TradeFix AI. We are committed to
            protecting your personal information and your right to privacy. This Privacy Policy explains
            how we collect, use, store, and share information about you when you use TradeFix AI.
          </p>
          <p className="mt-3">
            This policy is designed to comply with applicable data protection laws, including principles
            consistent with the General Data Protection Regulation (GDPR) and the Information Technology
            Act, 2000 (India). By using TradeFix AI, you consent to the practices described in this policy.
          </p>
        </section>

        {/* Data Collection */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            2. Data We Collect
          </h2>
          <p>We collect the following categories of personal data:</p>

          <div className="mt-4 space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="font-semibold text-gray-900">Account &amp; Identity Data</h3>
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm pl-2">
                <li>Full name (provided during registration)</li>
                <li>Email address (used as primary identifier)</li>
                <li>Mobile phone number (optional, for support communication)</li>
                <li>Password (stored as a cryptographic hash via Firebase Authentication)</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="font-semibold text-gray-900">Trading &amp; Usage Data</h3>
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm pl-2">
                <li>Trade records (symbol, date, entry/exit price, P&amp;L, emotion tags, notes)</li>
                <li>Risk management settings (daily loss limits, trade frequency limits)</li>
                <li>Psychology journal entries</li>
                <li>AI chat conversation history within the platform</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="font-semibold text-gray-900">Technical &amp; Log Data</h3>
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm pl-2">
                <li>Browser type and version</li>
                <li>Device type and operating system</li>
                <li>IP address and approximate location</li>
                <li>Login timestamps and session activity logs</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="font-semibold text-gray-900">Payment Data</h3>
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm pl-2">
                <li>Subscription plan and payment status (stored in Firestore)</li>
                <li>Payment ID and order ID from our payment gateway partner (for transaction verification)</li>
                <li>We do <strong>not</strong> store card numbers, CVV, or full banking details. All payment processing is handled by our payment gateway partner.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Firebase */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            3. How We Store Your Data
          </h2>
          <p>
            TradeFix AI uses <strong>Google Firebase</strong> for authentication and data storage:
          </p>
          <ol className="list-decimal list-inside mt-3 space-y-2 pl-2">
            <li><strong>Firebase Authentication</strong> — manages your login credentials securely. Passwords are never stored in plain text.</li>
            <li><strong>Cloud Firestore</strong> — a NoSQL database that stores your trades, risk settings, subscription status, and profile information in your private user document, accessible only to you.</li>
            <li>All data is stored on Google Cloud servers, which maintain SOC 2, ISO 27001, and other compliance certifications.</li>
            <li>Data is encrypted in transit (TLS/HTTPS) and at rest by Firebase.</li>
          </ol>
        </section>

        {/* Purpose of Use */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            4. Purpose of Data Usage
          </h2>
          <p>We use your personal data exclusively to:</p>
          <ol className="list-decimal list-inside mt-3 space-y-2 pl-2">
            <li>Provide and maintain the TradeFix AI service and all its features.</li>
            <li>Authenticate your identity and manage your account securely.</li>
            <li>Generate personalized AI insights, psychology analysis, and risk alerts based on your trade data.</li>
            <li>Process payments and manage subscription status via our payment gateway partner.</li>
            <li>Send transactional communications (account confirmations, subscription receipts, important notices).</li>
            <li>Detect and prevent fraud, unauthorized access, or misuse of the platform.</li>
            <li>Improve and develop our features and AI models (using aggregated, anonymized data only).</li>
            <li>Comply with applicable legal obligations.</li>
          </ol>
          <p className="mt-3">
            We do <strong>not</strong> use your personal trade data for advertising or sell it to third parties.
          </p>
        </section>

        {/* Security */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            5. Data Security Measures
          </h2>
          <p>We implement industry-standard security measures to protect your data:</p>
          <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
            <li>End-to-end HTTPS encryption for all data transmitted between your browser and our servers.</li>
            <li>Firebase Security Rules that enforce per-user data isolation — no user can access another user's data.</li>
            <li>Firebase Authentication for secure identity management.</li>
            <li>Regular security reviews and access controls.</li>
          </ul>
          <p className="mt-3">
            While we take reasonable precautions, no method of transmission over the Internet or electronic
            storage is 100% secure. We encourage you to use a strong, unique password and keep your
            credentials confidential.
          </p>
        </section>

        {/* Third-party sharing */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            6. No Third-Party Data Sharing
          </h2>
          <p>
            We do <strong>not</strong> sell, rent, or trade your personal information to any third party
            for marketing purposes.
          </p>
          <p className="mt-3">We may share data only in the following limited circumstances:</p>
          <ol className="list-decimal list-inside mt-3 space-y-2 pl-2">
            <li><strong>Service Providers:</strong> Google Firebase (data storage), our payment gateway partner (payment processing), and Google Cloud (infrastructure). These are bound by their own privacy policies and data processing agreements.</li>
            <li><strong>Legal Requirements:</strong> If required by law, court order, or government regulation, we may disclose information to comply with legal obligations.</li>
            <li><strong>Business Transfers:</strong> If TradeFix AI is acquired or merged, your data may be transferred as part of that transaction, with prior notice to you.</li>
          </ol>
        </section>

        {/* User Rights */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            7. Your Rights
          </h2>
          <p>You have the following rights regarding your personal data:</p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { right: 'Right to Access', desc: 'Request a copy of all personal data we hold about you.' },
              { right: 'Right to Correction', desc: 'Request correction of any inaccurate or incomplete data.' },
              { right: 'Right to Deletion', desc: 'Request deletion of your account and all associated data.' },
              { right: 'Right to Portability', desc: 'Receive your data in a structured, machine-readable format.' },
              { right: 'Right to Object', desc: 'Object to processing of your data for specific purposes.' },
              { right: 'Right to Withdraw Consent', desc: 'Withdraw consent at any time by deleting your account.' },
            ].map((item) => (
              <div key={item.right} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="font-semibold text-gray-900 text-sm">{item.right}</p>
                <p className="text-sm text-gray-600 mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-4">
            To exercise any of these rights, contact us at{' '}
            <a href="mailto:krishautomation.services@gmail.com" className="text-blue-600 hover:underline">
              krishautomation.services@gmail.com
            </a>. We will respond within 30 days.
          </p>
        </section>

        {/* Cookies */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            8. Cookie Usage
          </h2>
          <p>
            TradeFix AI uses minimal cookies and browser storage primarily for authentication purposes
            (Firebase Authentication session tokens). We do not use tracking cookies for advertising.
          </p>
          <p className="mt-3">
            You can control cookie behavior through your browser settings. Disabling cookies may affect
            certain platform functionality, including the ability to stay logged in.
          </p>
        </section>

        {/* Data Retention */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            9. Data Retention Policy
          </h2>
          <p>We retain your personal data for as long as your account is active or as needed to provide you services. Specifically:</p>
          <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
            <li><strong>Active accounts:</strong> Data is retained indefinitely while your account remains active.</li>
            <li><strong>Deleted accounts:</strong> Upon account deletion request, we delete your personal data within 30 days, except where retention is required by law.</li>
            <li><strong>Payment records:</strong> Transaction records may be retained for up to 7 years to comply with financial regulations.</li>
          </ul>
        </section>

        {/* Children's Privacy */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            10. Children's Privacy
          </h2>
          <p>
            TradeFix AI is intended for users who are 18 years of age or older. We do not knowingly collect
            personal information from individuals under 18. If you believe a minor has provided us with
            personal data, please contact us immediately and we will take steps to delete it.
          </p>
        </section>

        {/* Contact */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            11. Contact for Privacy Matters
          </h2>
          <p>For any privacy-related questions, requests, or complaints, please contact our Data Controller:</p>
          <div className="mt-4 bg-gray-50 rounded-lg p-5 border border-gray-200 space-y-2">
            <p><strong>Krish Automation Services</strong></p>
            <p>
              📧 Email:{' '}
              <a href="mailto:krishautomation.services@gmail.com" className="text-blue-600 hover:underline">
                krishautomation.services@gmail.com
              </a>
            </p>
            <p>
              💬 WhatsApp:{' '}
              <a href="https://wa.me/919373606511" className="text-blue-600 hover:underline">
                +91-9373606511
              </a>{' '}
              (Message only)
            </p>
            <p className="text-sm text-gray-500">Response within 30 days | Mon–Sat, 10 AM – 6 PM IST</p>
          </div>
        </section>

        {/* Back button */}
        <div className="pt-4 border-t border-gray-200">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <ArrowLeft size={16} />
            <span>Back to Dashboard</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
